/**
 * MediaPipe Performance Benchmark
 * Détermine automatiquement si CPU ou GPU offre les meilleures performances
 * et stocke le résultat dans le localStorage pour éviter de refaire le test
 */

import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";

export class MediaPipeBenchmark {
    constructor() {
        this.CACHE_KEY = 'mediapipe_benchmark_result';
        this.CACHE_VERSION_KEY = 'mediapipe_benchmark_version';
        this.CURRENT_VERSION = '1.0.0'; // Incrémenter pour forcer un nouveau benchmark
        this.CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours en millisecondes
    }

    /**
     * Obtient le delegate optimal (CPU ou GPU)
     * Vérifie d'abord le cache, sinon lance un benchmark rapide
     */
    async getOptimalDelegate() {
       
        const cachedResult = this.getCachedResult();
        
        if (cachedResult) {
        window.loader.setMessage('Récupération Configuration...');
         //   console.log(`🚀 Utilisation du delegate mis en cache: ${cachedResult.delegate} (${cachedResult.improvement}% plus rapide)`);
            return cachedResult.delegate;
        }

        console.log('🔄 Aucun résultat en cache, lancement du benchmark automatique...');
           window.loader.setMessage('Optimal configuration ( run only once) ');
        const result = await this.runQuickBenchmark();
        
        if (result) {
                 
            console.log(`✅ Benchmark terminé: ${result.delegate} est ${result.improvement}% plus rapide`);
            return result.delegate;
        }

        console.warn('⚠️ Échec du benchmark, utilisation du CPU par défaut');
        return 'CPU';
    }

    /**
     * Vérifie si un résultat valide existe en cache
     */
    getCachedResult() {
        try {
            const version = localStorage.getItem(this.CACHE_VERSION_KEY);
            const result = localStorage.getItem(this.CACHE_KEY);
            
            if (!result || version !== this.CURRENT_VERSION) {
                return null;
            }

            const parsed = JSON.parse(result);
            const now = Date.now();
            
            // Vérifier si le cache n'a pas expiré
            if (now - parsed.timestamp > this.CACHE_DURATION) {
                this.clearCache();
                return null;
            }

            return parsed;
        } catch (error) {
            console.warn('Erreur lors de la lecture du cache benchmark:', error);
            this.clearCache();
            return null;
        }
    }

    /**
     * Sauvegarde le résultat dans le cache
     */
    saveResult(delegate, cpuTime, gpuTime, improvement) {
        try {
            const result = {
                delegate,
                cpuTime,
                gpuTime,
                improvement,
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                hardwareConcurrency: navigator.hardwareConcurrency
            };

            localStorage.setItem(this.CACHE_KEY, JSON.stringify(result));
            localStorage.setItem(this.CACHE_VERSION_KEY, this.CURRENT_VERSION);
        } catch (error) {
            console.warn('Impossible de sauvegarder le résultat du benchmark:', error);
        }
    }

    /**
     * Efface le cache
     */
    clearCache() {
        localStorage.removeItem(this.CACHE_KEY);
        localStorage.removeItem(this.CACHE_VERSION_KEY);
    }

    /**
     * Lance un benchmark rapide (3 itérations) pour déterminer le meilleur delegate
     */
    async runQuickBenchmark() {
        const params = {
            iterations: 3,
            resolution: 320, // Résolution réduite pour un test rapide
            warmup: 1,
            numFaces: 1
        };

        try {
            const cpuResults = await this.benchmarkDelegate("CPU", params);
            const gpuResults = await this.benchmarkDelegate("GPU", params);

            const cpuWins = cpuResults.avg < gpuResults.avg;
            const winner = cpuWins ? 'CPU' : 'GPU';
            const improvement = Math.abs(cpuResults.avg - gpuResults.avg);
            const improvementPercent = Math.round((improvement / Math.max(cpuResults.avg, gpuResults.avg)) * 100);

            // Sauvegarder le résultat
            this.saveResult(winner, cpuResults.avg, gpuResults.avg, improvementPercent);

            return {
                delegate: winner,
                cpuTime: cpuResults.avg,
                gpuTime: gpuResults.avg,
                improvement: improvementPercent
            };

        } catch (error) {
            console.error('Erreur lors du benchmark:', error);
            return null;
        }
    }

    /**
     * Lance un benchmark complet pour des résultats plus précis
     */
    async runFullBenchmark() {
        const params = {
            iterations: 10,
            resolution: 640,
            warmup: 3,
            numFaces: 1
        };

        try {
            console.log('🔄 Benchmark complet en cours...');
            
            const cpuResults = await this.benchmarkDelegate("CPU", params);
            const gpuResults = await this.benchmarkDelegate("GPU", params);

            const cpuWins = cpuResults.avg < gpuResults.avg;
            const winner = cpuWins ? 'CPU' : 'GPU';
            const improvement = Math.abs(cpuResults.avg - gpuResults.avg);
            const improvementPercent = Math.round((improvement / Math.max(cpuResults.avg, gpuResults.avg)) * 100);

            // Sauvegarder le résultat
            this.saveResult(winner, cpuResults.avg, gpuResults.avg, improvementPercent);

            console.log(`✅ Benchmark complet terminé: ${winner} est ${improvementPercent}% plus rapide`);
            
            return {
                delegate: winner,
                cpuTime: cpuResults.avg,
                gpuTime: gpuResults.avg,
                improvement: improvementPercent,
                cpuResults,
                gpuResults
            };

        } catch (error) {
            console.error('Erreur lors du benchmark complet:', error);
            return null;
        }
    }

    /**
     * Crée une image factice pour le benchmark
     */
    async createDummyFrame(size) {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Créer un pattern réaliste simulant un visage
        const gradient = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
        gradient.addColorStop(0, '#ffdbac');
        gradient.addColorStop(0.5, '#e6b88a');
        gradient.addColorStop(1, '#d49c7a');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, size, size);
        
        // Ajouter du bruit pour simuler une vraie image
        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            const noise = (Math.random() - 0.5) * 20;
            data[i] = Math.max(0, Math.min(255, data[i] + noise));
            data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
            data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
        }
        ctx.putImageData(imageData, 0, 0);
        
        return canvas;
    }    /**
     * Benchmark un delegate spécifique (CPU ou GPU)
     */
    async benchmarkDelegate(delegateType, params) {
    const filesetResolver = await FilesetResolver.forVisionTasks("./wasm");
        
        const landmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
            baseOptions: {
                modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
                delegate: delegateType
            },
            outputFaceBlendshapes: false, // Désactivé pour des tests plus rapides
            runningMode: "VIDEO",
            smoothlandmarks: false,
            numFaces: params.numFaces
        });
        
        const dummyFrame = await this.createDummyFrame(params.resolution);
        const times = [];
        
        // Warm-up
        for (let i = 0; i < params.warmup; i++) {
            await landmarker.detectForVideo(dummyFrame, performance.now());
        }
        
        // Benchmark réel
        for (let i = 0; i < params.iterations; i++) {
            const start = performance.now();
            await landmarker.detectForVideo(dummyFrame, performance.now());
            const end = performance.now();
            times.push(end - start);
            
            // Petit délai pour éviter de bloquer l'UI
            await new Promise(resolve => setTimeout(resolve, 5));
        }
        
        landmarker.close();
        
        return {
            avg: times.reduce((a, b) => a + b, 0) / times.length,
            min: Math.min(...times),
            max: Math.max(...times),
            times: times
        };
    }

    /**
     * Force un nouveau benchmark en effaçant le cache
     */
    async forceBenchmark() {
        this.clearCache();
        return await this.getOptimalDelegate();
    }

    /**
     * Obtient les informations du cache actuel
     */
    getCacheInfo() {
        const cached = this.getCachedResult();
        if (!cached) return null;

        return {
            delegate: cached.delegate,
            improvement: cached.improvement,
            age: Math.round((Date.now() - cached.timestamp) / (1000 * 60 * 60)), // heures
            cpuTime: cached.cpuTime?.toFixed(2),
            gpuTime: cached.gpuTime?.toFixed(2)
        };
    }
}
